import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { isTokenExpired, isTokenExpiringSoon } from "@/lib/tellscale/token-expiry.ts";

import { useAuth } from "@/stores/auth";

/**
 * Set up axios interceptors for authentication and token refresh
 *
 * @param {AxiosInstance} axiosInstance - Axios instance to attach interceptors to
 */
export const setupAuthInterceptors = (axiosInstance: AxiosInstance): void => {
    // Request interceptor: Check token expiry and refresh if needed
    axiosInstance.interceptors.request.use(
        async (config) => await handleTokenRefresh(config),
        (error) => Promise.reject(error)
    );

    // Response interceptor: Handle 401 errors
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => await handleAuthError(error)
    );
}

/**
 * Request interceptor to check token expiry and refresh if needed
 *
 * @param {InternalAxiosRequestConfig} config - Axios request config
 * @returns {Promise<InternalAxiosRequestConfig>} Updated request config
 */
const handleTokenRefresh = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const auth = useAuth();

    if(isAuthEndpoint(config.url)) {
        return config;
    }

    if(!auth.isAuthenticated) {
        return config;
    }

    if(isTokenExpired(auth.expiresAt)) {
        console.warn('[Interceptor] Token expired, logging out');

        await auth.logout();

        return Promise.reject();
    }

    if(isTokenExpiringSoon(auth.expiresAt, 10)) {
        console.info('[Interceptor] Token expiring soon, refreshing proactively');

        try {
            await auth.refresh();
        }
        catch(error) {
            console.error('[Interceptor] Failed to refresh token', error);
        }
    }

    return config;
}

/**
 * Response interceptor to handle 401 authentication errors
 *
 * @param {AxiosError} error - Axios error
 * @returns {Promise<AxiosResponse>} Retry response or rejected promise
 */
const handleAuthError = async (error: AxiosError): Promise<AxiosResponse> => {
    const auth = useAuth();

    if(isAuthEndpoint(error.config?.url)) {
        return Promise.reject(error);
    }

    if(!auth.isAuthenticated) {
        return Promise.reject(error);
    }

    if(error.response?.status === 401) {
        console.info('[Interceptor] Received 401 error, attempting to authenticate');

        const success = await auth.authenticate(true);

        if(!success) {
            console.error('[Interceptor] Authentication failed, logging out');

            await auth.logout();

            return Promise.reject(error);
        }

        console.info('[Interceptor] Basic authentication successful');
    }

    return Promise.reject(error);
}

/**
 * Check if the URL is an authentication endpoint that should skip interceptors
 *
 * @param {string | undefined} url - Request URL
 * @returns {boolean} True if URL is an auth endpoint
 */
const isAuthEndpoint = (url: string | undefined): boolean => {
    if(!url) return false;

    const authEndpoints = [
        '/api/auth/promoter/refresh',
        '/api/auth/promoter/logout',
        '/api/me',
    ];

    return authEndpoints.some(endpoint => url.includes(endpoint));
}
